import {useEffect} from 'react';
import {useLocation} from 'react-router-dom';
import {useTypedAppSelector} from 'redux/hooks';
import {redactSensitiveUrlParams} from 'utils/sensitiveUrl';

type HubspotWindow = Window & typeof globalThis & {_hsq: any[]};

const getHubspotEventArray = (): any[] => {
  const w = window as HubspotWindow;
  /* eslint-disable no-underscore-dangle */
  if (!w._hsq) {
    w._hsq = [];
  }
  return w._hsq;
  /* eslint-enable no-underscore-dangle */
};

const hubspotSetVisitorEmail = (hsq: any[], email: string) => {
  if (process.env.NODE_ENV === 'development') {
    console.log('hubspotSetVisitorEmail', email);
  }
  hsq.push(['identify', {email}]);
};

const hubspotSetPath = (hsq: any[], path: string) => {
  // Redact one-time secrets (e.g. /change-password?token=..., /sign-up/?invite=...) before this
  // path is logged or sent to HubSpot's tracking beacon.
  const redactedPath = redactSensitiveUrlParams(path);
  if (process.env.NODE_ENV === 'development') {
    console.log('hubspotSetPath', redactedPath);
  }
  hsq.push(['setPath', redactedPath]);
};

const hubspotTrackPageView = (hsq: any[]) => {
  if (process.env.NODE_ENV === 'development') {
    console.log('hubspotTrackPageView');
  }
  hsq.push(['trackPageView']);
};

const hubspotRevokeCookieConsent = (hsq: any[]) => {
  if (process.env.NODE_ENV === 'development') {
    console.log('hubspotRevokeCookieConsent');
  }
  hsq.push(['revokeCookieConsent']);
};

const HubspotTrackPageView = () => {
  const profile = useTypedAppSelector(state => state.user.profile);
  const isProfileLoading = useTypedAppSelector(state => state.user.isProfileLoading);
  const location = useLocation();
  useEffect(() => {
    if (isProfileLoading) return;
    try {
      const hsq = getHubspotEventArray();
      if (profile?.email) {
        hubspotSetVisitorEmail(hsq, profile.email);
      }
      hubspotSetPath(hsq, location.pathname + location.search);
      hubspotTrackPageView(hsq);
    } catch (e) {
      console.error(e);
    }
  }, [location.pathname, location.search, profile?.email, isProfileLoading]);
  return null;
};

const getHubSpotDealUrl = (dealId: string): string | null => {
  if (!dealId) return null;
  const hubspotPortalId = import.meta.env.VITE_HUBSPOT_TRACKING_ACCOUNT_ID as string | undefined;
  if (!hubspotPortalId) return null;
  return `https://app.hubspot.com/contacts/${hubspotPortalId}/record/0-3/${dealId}`;
};

const getHubSpotTicketUrl = (ticketId: string): string | null => {
  if (!ticketId) return null;
  const hubspotPortalId = import.meta.env.VITE_HUBSPOT_TRACKING_ACCOUNT_ID as string | undefined;
  if (!hubspotPortalId) return null;
  return `https://app.hubspot.com/contacts/${hubspotPortalId}/record/0-5/${ticketId}`;
};

const getHubSpotCompanyUrl = (companyId: string): string | null => {
  if (!companyId) return null;
  const hubspotPortalId = import.meta.env.VITE_HUBSPOT_TRACKING_ACCOUNT_ID as string | undefined;
  if (!hubspotPortalId) return null;
  return `https://app.hubspot.com/contacts/${hubspotPortalId}/record/0-2/${companyId}`;
};

export {
  getHubspotEventArray,
  hubspotSetVisitorEmail,
  hubspotSetPath,
  hubspotTrackPageView,
  hubspotRevokeCookieConsent,
  HubspotTrackPageView,
  getHubSpotDealUrl,
  getHubSpotTicketUrl,
  getHubSpotCompanyUrl,
};
